import type { AdminDashboardOverview, AdminDashboardSection } from "../models/admin-dashboard.models";
import type { AdminMonitoringKpi, CronJobLogEnvironment } from "../models/admin-monitoring.models";
import { selectAdminDashboardMonitoringOverview } from "../utils/admin-dashboard.utils";
import { buildAdminUwhcMonitoringStats, toAdminUwhcGridRow } from "../utils/admin-uwhc.utils";
import { getAdminCrewReportStatistics } from "./admin-crew-report.service";
import { getAdminKeysSecretsStatus } from "./admin-keys-secrets.service";
import { getAdminFeatureMonitoring, getCronJobLogs } from "./admin-monitoring.service";
import { getAdminUwhcProviders } from "./admin-uwhc.service";

// Compose existing read APIs and keep full tables and sensitive credential responses out of dashboard state.
export const getAdminDashboardOverview = async (
  section: AdminDashboardSection,
  environment: CronJobLogEnvironment,
): Promise<AdminDashboardOverview> => {
  switch (section.id) {
    case "cronJobs":
      return getJobOverview(environment);
    case "crewReport":
      return getCrewOverview();
    case "uwhc":
      return getProviderOverview();
    case "keysSecrets":
      return getRotationOverview();
    default:
      return selectAdminDashboardMonitoringOverview(await getAdminFeatureMonitoring(section.id, environment), section);
  }
};

// === SCHEDULED JOBS ===

async function getJobOverview(environment: CronJobLogEnvironment): Promise<AdminDashboardOverview> {
  const response = await getCronJobLogs({
    environment,
    page: 1,
    pageSize: 1,
    sortBy: "created",
    sortDirection: "desc",
  });
  const logs = response.dailyCounts.reduce((total, day) => total + day.total, 0);
  const errors = response.dailyCounts.reduce((total, day) => total + day.errors, 0);

  return {
    metrics: [
      { key: "logs14d", label: "Logs · 14 days", value: logs },
      { key: "errors14d", label: "Logs with errors · 14 days", value: errors, tone: errors > 0 ? "danger" : "success" },
    ],
    dailyCounts: response.dailyCounts,
  };
}

// === CREW REPORTS ===

async function getCrewOverview(): Promise<AdminDashboardOverview> {
  const response = await getAdminCrewReportStatistics();
  const coverage = response.reportedVessels;

  return {
    metrics: [
      { key: "crewOnBoard", label: "Crew on board", value: response.crewOverview.totalCrewOnBoard },
      {
        key: "missingReports",
        label: "Vessels without reports",
        value: coverage.notReportedVesselCount,
        tone: coverage.notReportedVesselCount > 0 ? "warning" : "success",
      },
    ],
    breakdown: {
      key: "reportCoverage",
      title: "Running vessel reporting",
      kind: "bar",
      items: [
        { label: "Reported", value: coverage.reportedVesselCount },
        { label: "Not reported", value: coverage.notReportedVesselCount, tone: "warning" },
      ],
    },
  };
}

// === HULL CLEANING PROVIDERS ===

async function getProviderOverview(): Promise<AdminDashboardOverview> {
  const response = await getAdminUwhcProviders();
  const stats = buildAdminUwhcMonitoringStats(response.rows.map((row) => toAdminUwhcGridRow(row)));

  return {
    metrics: stats.metrics
      .filter((metric) => metric.key === "providers" || metric.key === "screened")
      .map((metric): AdminMonitoringKpi => ({ ...metric, tone: metric.tone === "accent" ? "neutral" : metric.tone })),
    breakdown: { key: "providerCountries", title: "Top provider countries", kind: "bar", items: stats.topCountries },
  };
}

// === CREDENTIAL ROTATION ===

async function getRotationOverview(): Promise<AdminDashboardOverview> {
  const status = await getAdminKeysSecretsStatus();
  const rotationNeedsAttention = status.isRotationOverdue || status.hasPendingRotation;
  const rotationLabel = status.isRotationOverdue
    ? "Overdue"
    : status.hasPendingRotation
      ? "Pending"
      : status.lastRotation
        ? "Up to date"
        : "Not recorded";
  const mailboxLabel = !status.mailboxConfigured ? "Not set" : status.mailboxScanError ? "Unavailable" : "Configured";

  return {
    metrics: [
      {
        key: "rotation",
        label: "Rotation",
        value: rotationLabel,
        tone: rotationNeedsAttention ? "danger" : status.lastRotation ? "success" : "warning",
      },
      {
        key: "mailbox",
        label: "Mailbox",
        value: mailboxLabel,
        tone: !status.mailboxConfigured || status.mailboxScanError ? "warning" : "neutral",
      },
    ],
    note: rotationNeedsAttention ? "Review the latest rotation batch." : "Manage rotation and mailbox settings.",
  };
}
